<template>
  <detail
    :on-manager="onManager"
    :data="data"
    :base-info="baseInfo"
    :extra-info="extraInfo"
    :hiddenKeys="hiddenKeys" />
</template>

<script>
import _ from 'lodash'
import { mapGetters } from 'vuex'
import * as R from 'ramda'
import { numerify } from '@/filters'
import {
  getTimeTableColumn,
} from '@/utils/common/tableColumn'
import TagDetailColumn from '@/sections/TagDetailColumn'
import BillProgress from '@Bill/sections/BillProgress'
import { currencyUnitMap } from '@/constants/currency'
import {
  getBudgetCycleTableColumn,
  getBillingTypeTableColumn,
  getBudgetAmountTableColumn,
  getBudgetResourceTypeTableColumn,
  getBudgetStatusTableColumn,
  getBudgetBrandTableColumn,
  getBudgetAccountTableColumn,
  getBudgetCloudProividerTableColumn,
  getBudgetRegionTableColumn,
  // getBudgetDomainTableColumn,
  getBudgetProjectTableColumn,
  getBudgetStartDateTableColumn,
  getBudgetEndDateTableColumn,
  alertFrequencyColumn,
  conditionColumn,
  getVerifiedContactTypesTableColumn,
} from '../utils/columns'

export default {
  name: 'BudgetListDetailDetail',
  components: [
    BillProgress,
  ],
  props: {
    data: {
      type: Object,
      required: true,
    },
    onManager: {
      type: Function,
      required: true,
    },
  },
  data () {
    return {
      hiddenKeys: ['status', 'project_domain', 'tenant'],
      alertsColumns: [
        {
          field: 'date_info',
          title: this.$t('bill.budget_date_info'),
          showOverflow: 'ellipsis',
          minWidth: 100,
          formatter: ({ row }) => {
            return row.date_info
          },
        },
        {
          field: 'stat_value',
          title: this.$t('bill.budget_alert_type_cost'),
          showOverflow: 'ellipsis',
          minWidth: 100,
          formatter: ({ row }) => {
            const currencySign = currencyUnitMap[this.data.currency]?.sign || currencyUnitMap.CNY.sign
            return `${currencySign} ${numerify(row.stat_value, '0,0.00')}`
          },
        },
        {
          field: 'amount',
          title: this.$t('bill.budget_amount'),
          minWidth: 100,
          formatter: ({ row }) => {
            if (this.data.amount) {
              const currencySign = currencyUnitMap[this.data.currency]?.sign || currencyUnitMap.CNY.sign
              return `${currencySign} ${numerify(this.data.amount, '0,0.00')}`
            }
            return '-'
          },
        },
        {
          field: 'budget_alert_amount',
          title: this.$t('bill.budget_alert_amount'),
          minWidth: 100,
          formatter: ({ row }) => {
            const currencySign = currencyUnitMap[this.data.currency]?.sign || currencyUnitMap.CNY.sign
            return `${currencySign} ${numerify(this.data.amount - row.stat_value, '0,0.00')}`
          },
        },
        {
          field: 'budget_alert_percent',
          title: this.$t('bill.budget_alert_percent'),
          minWidth: 100,
          // formatter: ({ row }) => {
          //   return `${((row.stat_value / this.data.amount) * 100).toFixed(2)}%`
          // },
          slots: {
            default: ({ row, column }) => {
              const percentage = (row.stat_value / this.data.amount) * 100
              return [
                <BillProgress percentage={ percentage } colors={ ['#52c41a', 'rgb(250, 173, 20)', 'rgb(245, 34, 45)'] } ranges={ [60, 90] }></BillProgress>,
                <span>{ percentage.toFixed(2) }%</span>,
              ]
            },
          },
        },
      ],
      baseInfo: [
        getBudgetStatusTableColumn(),
        getBudgetProjectTableColumn({ title: this.$t('bill.budget_project') }),
        getBudgetCycleTableColumn(),
        getBillingTypeTableColumn(),
        getBudgetAmountTableColumn(),
        getBudgetStartDateTableColumn(),
        getBudgetEndDateTableColumn(),
        getBudgetBrandTableColumn(),
        getBudgetAccountTableColumn(),
        getBudgetCloudProividerTableColumn(),
        getBudgetRegionTableColumn(),
        getBudgetResourceTypeTableColumn(),
        {
          title: this.$t('bill.text_79'),
          field: 'user_tag',
          slots: {
            default: ({ row }, h) => {
              return [
                h(TagDetailColumn, {
                  props: {
                    showEdit: false,
                    type: 'user',
                    row,
                    metadata: _.get(row, 'metadata', {}),
                    resource: 'server',
                    columns: () => this.alertsColumns,
                    tipName: this.$t('bill.text_79'),
                    customTitle: this.getMatchText(row),
                  },
                }),
              ]
            },
          },
        },
        getTimeTableColumn({ field: 'created_at', title: this.$t('common.createdAt') }),
        getTimeTableColumn({ field: 'updated_at', title: this.$t('common.updatedAt') }),
      ],
      users: [],
      billAnalysises: [],
    }
  },
  computed: {
    ...mapGetters(['scope']),
    extraInfo () {
      return [
        {
          title: this.$t('bill.budget_alert_info_title'),
          field: 'alerts',
          slots: {
            default: ({ row }, h) => {
              return [
                <vxe-grid class="mb-2" data={ this.billAnalysises } columns={ this.alertsColumns } />,
              ]
            },
          },
        },
        {
          title: this.$t('bill.budget_alert_amount_title'),
          items: [
            {
              field: 'amount',
              title: this.$t('bill.budget_alert_threshold'),
              formatter: ({ cellValue, row }) => {
                if (!R.isNil(row.alerts) && !R.isEmpty(row.alerts)) {
                  const alert = row.alerts[0]
                  const currencySign = currencyUnitMap[row.currency]?.sign || currencyUnitMap.CNY.sign
                  if (alert.alert_threshold_type === 'amount') {
                    return `${this.$t('bill.budget_alert_type_cost2')}${currencySign} ${alert.alert_threshold}`
                  } else if (alert.alert_threshold_type === 'percent') {
                    return `${this.$t('bill.budget_alert_type_cost2')}${alert.alert_threshold}%`
                  }
                  return '-'
                }
                return '-'
              },
            },
            {
              field: 'users',
              title: this.$t('bill.budget_alert_user'),
              formatter: ({ cellValue, row }) => {
                if (!R.isNil(this.users) && !R.isEmpty(this.users)) {
                  return this.users.map(v => v.name).join(',')
                }
                return '-'
              },
            },
            alertFrequencyColumn(),
            conditionColumn(),
            getVerifiedContactTypesTableColumn(),
          ],
        },
      ]
    },
  },
  created () {
    if (!R.isNil(this.data.alerts) && !R.isEmpty(this.data.alerts)) {
      this.getUsers()
    }
    this.getBillAnalysisesData()
  },
  methods: {
    getMatchText (row) {
      if (!row.metadata) {
        return ''
      }
      if (row.match_all) {
        return this.$t('bill.text_283')
      } else {
        return this.$t('bill.text_284')
      }
    },
    async getUsers () {
      try {
        const user_ids = this.data.alerts[0].user_ids
        const response = await new this.$Manager('users', 'v1').list({
          params: {
            filter: `id.in(${user_ids})`,
            scope: this.scope,
          },
        })
        this.users = response.data.data || []
      } catch (error) {
        throw error
      }
    },
    getBillAnalysisesData () {
      const { id, scope, period_type, project, project_domain, currency, start_date, end_date, is_exchanged_currency } = this.data
      const params = {
        budget_id: id,
        query_type: 'expense_trend',
        scope: this.scope,
        data_type: period_type,
        filter: `currency.equals('${currency}')`,
        start_date: this.$moment(start_date).format('YYYY-MM-DD') + 'TZ',
        end_date: this.$moment(end_date).format('YYYY-MM-DD') + 'TZ',
        ...this.getFilterParams(),
      }

      if (period_type === 'day') {
        params.start_date = this.$moment(start_date).subtract(3, 'days').format('YYYY-MM-DD') + 'TZ'
      } else if (period_type === 'month') {
        params.end_date = this.$moment(end_date).endOf('month').format('YYYY-MM-DD') + 'TZ'
      } else if (period_type === 'year') {
        params.end_date = this.$moment(end_date).endOf('year').format('YYYY-MM-DD') + 'TZ'
      }
      if (scope === 'project') {
        params.project = project
      } else if (scope === 'domain') {
        params.domain = project_domain
        // 二次定价参数调整
        params.cost_conversion = true
      }
      if (is_exchanged_currency) {
        params.filter = ''
        params.exchanged_currency = currency
        params.is_exchanged_currency = true
      }
      new this.$Manager('bill_analysises', 'v1').list({ params })
        .then(({ data: { data = [] } }) => {
          this.billAnalysises = data
        })
        .catch(() => {
          this.billAnalysises = []
        })
    },
    getFilterParams () {
      const filterParams = ['brand', 'cloudaccount_id', 'cloudprovider_id', 'region_id', 'resource_type']
      const params = {}
      filterParams.forEach(v => {
        const newKey = v === 'cloudaccount_id' ? 'account_id' : v
        if (this.data[v]) {
          params[newKey] = this.data[v]
        }
      })
      return params
    },
  },
}
</script>
